// API Endpoint URLs for Bloom IQ Backend
// Base URL is configured via environment variables:
// - Development: bloom-webapp-infra-dev (set in .env.development)
// - Production: bloom-webapp-infra-prod (set in .env.production)

const API_BASE = import.meta.env.VITE_API_BASE_URL;

// User Management Endpoints
export const ADD_USER_URL = `${API_BASE}/add_user_db`;
export const GET_USER_DETAILS = `${API_BASE}/get_user_details`;
export const UPDATE_USER_DETAILS = `${API_BASE}/edit_username_surname`;
export const LOG_USR_ACTIVITY = `${API_BASE}/log_user_activity`;

// Analytics Endpoints
export const LOAD_CHART_DATA = `${API_BASE}/load_all_charts_endpoint`;
export const GET_COMMODITIES = `${API_BASE}/get_commodities`;

// Individual Chart Endpoints
export const USA_PRICE_CHART = `${API_BASE}/usa_price_chart_endpoint`;
export const USA_TERMINAL_PRICE_CHART = `${API_BASE}/usa_terminal_chart_endpoint`;
export const USA_DAILY_VOLUME_CHART = `${API_BASE}/usa_daily_volume_chart_endpoint`;
export const USA_HISTORIC_VOLUME_CHART = `${API_BASE}/usa_historic_volume_chart_endpoint`;
export const USA_PREDICTIONS_CHART = `${API_BASE}/usa_predictions_chart_endpoint`;
export const USA_CLUSTER_CHART = `${API_BASE}/usa_cluster_chart_endpoint`;
export const PRODUCING_REGIONS_CHART = `${API_BASE}/producing_regions_chart_endpoint`;
export const GET_COMMODITY_SKU_PRICE = `${API_BASE}/get_commodity_sku_price`;
export const GET_COMMODITY_SKU_VOLUME = `${API_BASE}/get_commodity_sku_volume`;

// Terminal Price Pulse table (weekly ISO weeks; not the line chart)
export const TERMINAL_PRICE_PULSE_TABLE = `${API_BASE}/terminal_price_pulse_table_endpoint`;

// Smart Price Classifier Endpoints
export const PRICE_CLASSIFIER_DATA = `${API_BASE}/price_classifier_data_endpoint`;
export const PRICE_CLASSIFIER_CLASSIFY = `${API_BASE}/price_classifier_classify_endpoint`;

// FOB Price Dashboard Endpoints
export const FOB_PRICE_AVG = `${API_BASE}/fob_price_avg_endpoint`;
export const FOB_PRICE_BY_ORIGIN = `${API_BASE}/fob_price_by_origin_endpoint`;
export const FOB_SEASONALITY = `${API_BASE}/fob_seasonality_endpoint`;

// Market Share Dashboard Endpoints
export const MARKET_SHARE_CHART = `${API_BASE}/market_share_chart_endpoint`;
export const MARKET_SHARE_SUMMARY = `${API_BASE}/market_share_summary_endpoint`;

// Chat/BiQ Endpoints
export const TALK_TO_BIQ = `${API_BASE}/talk_to_biq`;
export const BIQ = `${API_BASE}/cerebras_biq`;
export const MARK_AS_OFFENSIVE = `${API_BASE}/report_chat_answer`;
export const ONE_CLICK_REPORT = `${API_BASE}/one_click_report`;
// BiQ response feedback (Cloud Function). In dev, Vite proxies /feedback to avoid CORS.
export const BIQ_FEEDBACK_URL =
  import.meta.env.VITE_BIQ_FEEDBACK_URL ||
  (import.meta.env.DEV ? '/feedback' : `${API_BASE}/feedback`);

// Conversation sessions (Cloud Function `conversation_sessions`) — list/
// restore/rename/delete for the BiQ "Recent chats" sidebar. Separate service
// from BIQ_AGENTIC/container_chat_app (see that service's README,
// "Persistent conversations" section, for the split rationale). This is a
// BASE (empty in dev — callers' own paths already start with /conversations
// and hit the Vite proxy below directly), not one fixed endpoint like
// BIQ_FEEDBACK_URL — callers append /conversations, /conversations/<id>,
// /conversations/<id>/messages themselves.
// NOTE(prod-deploy): conversation_sessions CF NOT yet deployed to
// bloom-webapp-infra-prod (verified 2026-08-18, URL 404s). The prod default
// below follows the *.cloudfunctions.net convention but is unconfirmed;
// deploy + confirm (or override via VITE_BIQ_CONVERSATIONS_URL in
// .env.production) before next prod release.
// GOTCHA: import.meta.env.DEV tracks NODE_ENV and is FALSE in ALL vite
// builds, including --mode development. DEV may only gate the
// vite-dev-server -> proxy case; built bundles must branch on
// import.meta.env.MODE. Do not reintroduce "DEV ? proxy : prodDefault".
const BIQ_CONVERSATIONS_CLOUD_DEV =
  'https://us-central1-bloom-webapp-infra-dev.cloudfunctions.net/conversation_sessions';
const BIQ_CONVERSATIONS_CLOUD_PROD =
  'https://us-central1-bloom-webapp-infra-prod.cloudfunctions.net/conversation_sessions';
const defaultBiqConversationsUrl =
  import.meta.env.MODE === 'production' ? BIQ_CONVERSATIONS_CLOUD_PROD : BIQ_CONVERSATIONS_CLOUD_DEV;
export const BIQ_CONVERSATIONS_BASE =
  import.meta.env.DEV
    ? ''
    : import.meta.env.VITE_BIQ_CONVERSATIONS_URL || defaultBiqConversationsUrl;

// =============================================================================
// BiQ Agentic Chat Endpoint
// =============================================================================
// Model selection controlled by backend DEFAULT_MODEL configuration
// Original Firebase Functions endpoint:
// export const BIQ_AGENTIC = `${API_BASE}/chat_endpoint`;

// Container chat app (Cloud Run). Not public-biq and not the legacy chat_endpoint.
const BIQ_AGENTIC_CLOUD_DEV =
  'https://container-chat-app-44434484314.us-central1.run.app/chat';
const BIQ_AGENTIC_CLOUD_PROD =
  'https://container-chat-app-423149367180.us-central1.run.app/chat';
const defaultBiqAgenticUrl =
  import.meta.env.MODE === 'production' ? BIQ_AGENTIC_CLOUD_PROD : BIQ_AGENTIC_CLOUD_DEV;

// Uses VITE_BIQ_LOCAL=true in .env.local to switch to localhost for testing.
// In `npm run dev`, requests go through the Vite proxy at /api/agentic/* (same-origin).
export const BIQ_AGENTIC = import.meta.env.DEV
    ? '/api/agentic/chat'
    : import.meta.env.VITE_BIQ_LOCAL === 'true'
    ? 'http://localhost:8080/chat'
    : (import.meta.env.VITE_BIQ_AGENTIC_URL || defaultBiqAgenticUrl);

/**
 * Base for container-chat-app routes (/chat, /report/pdf, etc.).
 * In dev this is the Vite proxy prefix (/api/agentic); in prod it is the Cloud Run origin.
 */
export function getBiqAgenticServiceBase() {
  if (typeof BIQ_AGENTIC === 'string' && BIQ_AGENTIC.startsWith('/')) {
    return BIQ_AGENTIC.replace(/\/$/, '').replace(/\/chat$/, '');
  }
  try {
    const u = new URL(BIQ_AGENTIC);
    const prefix = u.pathname.replace(/\/chat\/?$/, '').replace(/\/$/, '');
    return `${u.origin}${prefix}`;
  } catch {
    const u = new URL(defaultBiqAgenticUrl);
    const prefix = u.pathname.replace(/\/chat\/?$/, '').replace(/\/$/, '');
    return `${u.origin}${prefix}`;
  }
}

//
// Chart types supported (via render_chart tool):
//   - "line"    : Time series and trend visualization
//   - "bar"     : Comparison across categories
//   - "area"    : Cumulative data over time
//   - "pie"     : Distribution/proportion visualization
//   - "scatter" : Correlation analysis between variables
//   - "map"     : Geographic visualization with status colors (Leaflet)
//
// Chart features:
//   - Interactive tooltips on hover
//   - Zoom/pan on map charts
//   - Filter dropdowns for data filtering (when filters provided)
//   - Subtitle/description support
//   - Legend with status colors (map charts)

// Alert Endpoints - Default Alerts
export const GET_DEFAULT_ALERTS = `${API_BASE}/load_default_alert_endpoint`;
export const CREATE_DEFAULT_ALERT = `${API_BASE}/add_default_alert_endpoint`;
export const UPDATE_DEFAULT_ALERTS = `${API_BASE}/update_default_alert_endpoint`;
export const DELETE_DEFAULT_ALERT = `${API_BASE}/remove_default_alert_endpoint`;

// Alert Endpoints - Custom Alerts
export const GET_CUSTOM_ALERTS = `${API_BASE}/load_custom_alert_endpoint`;
export const CREATE_CUSTOM_ALERT = `${API_BASE}/add_custom_alert_endpoint`;
export const UPDATE_CUSTOM_ALERT = `${API_BASE}/update_custom_alert_endpoint`;
export const DELETE_CUSTOM_ALERT = `${API_BASE}/remove_custom_alert_endpoint`;

// Alert Notifications
export const GET_DEFAULT_ALERTS_NOTIFICATIONS = `${API_BASE}/load_default_alerts_notifications_endpoint`;
export const GET_CUSTOM_ALERTS_NOTIFICATIONS = `${API_BASE}/load_custom_alerts_notifications_endpoint`;
export const WEATHER_DIGEST_ALERTS = `${API_BASE}/load_default_alerts_notifications_endpoint_HOMEPAGE`;

// Extreme Weather Alerts for Homepage
export const EXTREME_WEATHER_ALERTS = `${API_BASE}/load_extreme_weather_alerts_homepage_endpoint`;

// Market Insights Cloud Run Endpoint
export const MARKET_INSIGHTS = import.meta.env.VITE_MARKET_INSIGHTS_URL
    || 'https://market-insights-44434484314.us-central1.run.app/insights';

// Regions and Weather
export const GET_REGIONS_BY_ALERT_TYPE_AND_COMMODITY = `${API_BASE}/get_region`;
export const GET_WEATHER_REGIONS = `${API_BASE}/load_weather_regions`;

// Public BiQ Conversion Tracking
export const EXCHANGE_AUTH_CODE = `${API_BASE}/exchange_auth_code`;
export const RECORD_LOGIN_CONVERSION = `${API_BASE}/record_login_conversion`;

// Authentication Endpoints
export const REGISTER_USER = ADD_USER_URL; // Uses existing add user endpoint
export const SEND_VERIFICATION_EMAIL = `${API_BASE}/send_verification_email`;
export const VERIFY_EMAIL_CODE = `${API_BASE}/verify_email_code`;
export const SEND_PASSWORD_RESET = `${API_BASE}/send_password_reset`;

// Language Preferences
export const GET_LANGUAGE = `${API_BASE}/get_language`;
export const SET_LANGUAGE = `${API_BASE}/set_language`;

// Subscription Endpoints
export const GET_SUBSCRIPTION_DETAILS = `${API_BASE}/get_subscription_details`;
export const GET_PAYMENT_METHOD = `${API_BASE}/get_payment_method`;
export const GET_PAYMENT_METHOD_PORTAL = `${API_BASE}/get_payment_method_portal`;
export const GET_RECEIPT = `${API_BASE}/get_receipt`;
export const LIST_RECEIPTS = `${API_BASE}/list_receipts`;
export const GET_INVOICE_FILE = `${API_BASE}/get_invoice_file`;
export const VALIDATE_PROMO_CODE = `${API_BASE}/validate_promo_code_endpoint`;
export const APPLY_PROMO_CODE = `${API_BASE}/apply_promo_code_endpoint`;
export const GET_TIER_LIMITS = `${API_BASE}/get_tier_limits`;
export const CANCEL_SUBSCRIPTION = `${API_BASE}/cancel_subscription`;
export const BEGIN_PROMO_SWITCH = `${API_BASE}/begin_promo_switch`;
export const PREPARE_CHECKOUT = `${API_BASE}/prepare_checkout`;

// Usage Tracking Endpoints
export const GET_USAGE = `${API_BASE}/get_usage`;
export const GET_USAGE_STATS = `${API_BASE}/get_usage_stats`;
export const TRACK_USAGE = `${API_BASE}/track_usage`;
export const BUY_EXTRA_TOKENS = `${API_BASE}/buy_extra_tokens`;
export const GET_TOKEN_PACKAGES = `${API_BASE}/get_token_packages`;

// Analytics View Tracking Endpoint
export const INCREMENT_ANALYTICS_VIEW = `${API_BASE}/increment_analytics_view`;
/** Telemetry only — does not increment usage.analytics_views (BigQuery / bloom-monitoring). */
export const LOG_ANALYTICS_LAYOUT_USAGE = `${API_BASE}/log_analytics_layout_usage`;

// Appearance/Theme Endpoints
export const SET_APPEARANCE = `${API_BASE}/set_appearance`;
export const SET_PREFERRED_COMMODITIES = `${API_BASE}/set_preferred_commodities`;

// Tutorial Endpoints
export const SET_TUTORIAL_COMPLETED = `${API_BASE}/set_tutorial_completed_endpoint`;

// WhatsApp Linking Endpoints
export const LINK_WHATSAPP = `${API_BASE}/link_whatsapp`;
export const REMOVE_WHATSAPP = `${API_BASE}/remove_whatsapp`;
export const GET_WHATSAPP_STATUS = `${API_BASE}/get_whatsapp_status`;
export const SEND_WHATSAPP_WELCOME = `${API_BASE}/send_whatsapp_welcome`;

// Welcome Email Endpoint
export const SEND_WELCOME_EMAIL = `${API_BASE}/send_welcome_email_endpoint`;

// Notification Endpoints
export const GET_NOTIFICATIONS = `${API_BASE}/get_notifications`;
export const MARK_NOTIFICATION_READ = `${API_BASE}/mark_notification_read`;
export const MARK_ALL_NOTIFICATIONS_READ = `${API_BASE}/mark_all_notifications_read`;
export const GET_UNREAD_NOTIFICATION_COUNT = `${API_BASE}/get_unread_notification_count`;

// Procurement Planner — in dev, use Vite proxy to avoid CORS (see vite.config.js).
const PROCUREMENT_API_BASE =
  import.meta.env.VITE_PROCUREMENT_API_BASE || API_BASE;

const procurementUrl = (path) =>
  import.meta.env.DEV ? path : `${PROCUREMENT_API_BASE}${path}`;

const PROCUREMENT_METRICS_PATH = '/procurement_metrics_endpoint';
export const PROCUREMENT_METRICS = procurementUrl(PROCUREMENT_METRICS_PATH);

const PROCUREMENT_GET_GROWERS_PATH = '/procurement_get_growers';
export const PROCUREMENT_GET_GROWERS = procurementUrl(PROCUREMENT_GET_GROWERS_PATH);

const PROCUREMENT_SAVE_GROWERS_PATH = '/procurement_save_growers';
export const PROCUREMENT_SAVE_GROWERS = procurementUrl(PROCUREMENT_SAVE_GROWERS_PATH);

const PROCUREMENT_REMOVE_GROWER_PATH = '/procurement_remove_grower';
export const PROCUREMENT_REMOVE_GROWER = procurementUrl(PROCUREMENT_REMOVE_GROWER_PATH);

// Cargo Shipments Endpoints
export const CARGO_SHIPMENTS = `${API_BASE}/cargo_shipments_endpoint`;

// Sea Tracking Endpoints (optional local override — do not reuse VITE_API_BASE_URL)
const SEA_TRACKING_BASE = import.meta.env.VITE_SEA_TRACKING_BASE_URL || API_BASE;
export const SEA_TRACKING_ACTIVE = `${SEA_TRACKING_BASE}/sea_tracking_active`;
export const SEA_TRACKING_HISTORY = `${SEA_TRACKING_BASE}/sea_tracking_history`;
export const SEA_TRACKING_CARRIERS = `${SEA_TRACKING_BASE}/sea_tracking_carriers`;
export const SEA_TRACKING_CONTAINER_SEARCH = `${SEA_TRACKING_BASE}/sea_tracking_container_search`;

// Importer daily PDF mint (slim CF; returns short-lived GCS V4 URL).
const IMPORTER_REPORT_BASE =
  import.meta.env.VITE_IMPORTER_REPORT_BASE_URL || API_BASE;
export const IMPORTER_REPORT_DOWNLOAD = `${IMPORTER_REPORT_BASE}/importer_report_download`;
export const IMPORTER_REPORT_PREVIEW = `${IMPORTER_REPORT_BASE}/importer_report_preview`;

// Air + Truck Tracking Endpoints. Separate functions from sea
// because neither mode shares the sea pipeline: each is a plain read of its
// own BigQuery view.
const AIR_TRACKING_BASE = import.meta.env.VITE_AIR_TRACKING_BASE_URL || API_BASE;
export const AIR_TRACKING_ACTIVE = `${AIR_TRACKING_BASE}/air_tracking_active`;
const TRUCK_TRACKING_BASE = import.meta.env.VITE_TRUCK_TRACKING_BASE_URL || API_BASE;
export const TRUCK_TRACKING_ACTIVE = `${TRUCK_TRACKING_BASE}/truck_tracking_active`;

// =============================================================================
// OpenWeatherMap - Weather Map Feature
// =============================================================================
// Maps 1.0 (free): Current weather tiles
// Maps 2.0 (~$40/mo): 10-day forecast with time parameter
// Docs: https://openweathermap.org/api/weathermaps
export const OPENWEATHERMAP_TILE_URL = 'https://tile.openweathermap.org/map';
export const OPENWEATHERMAP_TILE_URL_V2 = 'https://maps.openweathermap.org/maps/2.0/weather';

// =============================================================================
// CARTO Basemap Tiles
// =============================================================================
// Raster (PNG) light/dark basemap tiles for react-leaflet <TileLayer>, shared
// across BiQ.jsx, WeatherMap.jsx, ProducingRegionsMap.jsx, UnifiedMapContainer.jsx,
// ShipTrackingMap.jsx. CARTO now requires a `key` query param (NOT `api_key` —
// that param is silently ignored) or unkeyed requests render an "API KEY
// REQUIRED" watermark tile. Docs: https://docs.carto.com/faqs/carto-basemaps
export const CARTO_MAP_KEY = import.meta.env.VITE_CARTO_MAP_KEY;
export const CARTO_LIGHT_URL = `https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${CARTO_MAP_KEY ?? ''}`;
export const CARTO_DARK_URL = `https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=${CARTO_MAP_KEY ?? ''}`;
